import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTeamsUnderManager, isOrphanUnassignedTeam } from '../utils/teamHierarchy';
import { sortUsersByName } from '../utils/users';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SpinnerGap, Users, UserCircle, CaretDown, CaretUp, Crown, Phone, EnvelopeSimple, DownloadSimple, ArrowLeft, PencilSimple, UsersThree, X, MagnifyingGlass, Plus } from '@phosphor-icons/react';
import { ExportPreviewModal } from '../components/ExportPreviewModal';
import { fmtINR, fmtUSD } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeamOverviewPage = () => {
  const navigate = useNavigate();
  const { user, isManager, isLead, isSuperadmin, isCeo } = useAuth();
  const [overviewData, setOverviewData] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedManagers, setExpandedManagers] = useState({});
  const [expandedTeams, setExpandedTeams] = useState({});
  const [search, setSearch] = useState('');
  const [showExport, setShowExport] = useState(false);
  const [showRmModal, setShowRmModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [rmForm, setRmForm] = useState({ reporting_managers: [], onsite_managers: [] });
  const [savingRm, setSavingRm] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '', department: 'sales', lead_id: '', manager_id: '', assistant_manager_id: '',
    member_ids: [], sub_teams: [], budget: 0, reporting_managers: [], onsite_managers: [],
  });
  const [memberSearch, setMemberSearch] = useState('');
  const [rmSearch, setRmSearch] = useState('');
  const [editError, setEditError] = useState('');

  const isAdmin = isSuperadmin || isCeo;
  const hideAmounts = !isSuperadmin && !isCeo;

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [overviewRes, usersRes, allUsersRes] = await Promise.all([
        axios.get(`${API_URL}/api/team-overview-data`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/teams/users/available`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setOverviewData(overviewRes.data);
      setUsersData(usersRes.data || []);
      setAllUsers(sortUsersByName(allUsersRes.data || []));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const userMap = {};
  for (const u of usersData) { if (u.id) userMap[u.id] = u; }

  const rawTeams = overviewData?.teams || [];
  const teams = rawTeams
    .filter(t => !t.name?.toLowerCase().includes('direct'))
    .filter(t => !isOrphanUnassignedTeam(t, userMap, rawTeams))
    .map(team => ({
    ...team,
    members: (team.members || []).map(m => ({
      ...m,
      ...(userMap[m.id] ? { phone: userMap[m.id].phone || userMap[m.id].direct_number || m.phone } : {}),
    })),
  }));

  const getRoleLabel = (role) => {
    const map = { sales_recruiter: 'Recruiter', sales_technical_recruiter: 'Recruiter', sales_lead: 'Lead', sales_manager: 'Manager', sales_assistant_manager: 'Asst. Mgr', technical_recruiter: 'Recruiter', recruiting_lead: 'Lead', recruiting_manager: 'Manager', recruiting_assistant_manager: 'Asst. Mgr', superadmin: 'Admin', ceo: 'CEO', onsite_manager: 'Onsite Mgr' };
    return map[role] || role?.replace(/_/g, ' ');
  };

  // Group by Organization -> Manager -> Teams
  const groupByOrganization = (teamsList) => {
    const groups = {};
    for (const t of teamsList) {
      const mgr = userMap[t.manager_id];
      const orgKey = t.organization || mgr?.organization || 'Unassigned';
      if (!groups[orgKey]) groups[orgKey] = { organization: orgKey, managers: {} };
      const mgrId = t.manager_id;
      const mgrName = mgr?.name || t.manager_name || 'Unassigned Manager';
      if (!groups[orgKey].managers[mgrName]) {
        groups[orgKey].managers[mgrName] = { manager: mgr, manager_id: mgrId, manager_name: mgrName, teams: [] };
      }
      groups[orgKey].managers[mgrName].teams.push(t);
    }
    return Object.values(groups).map(org => ({
      ...org,
      managers: Object.values(org.managers),
      teams: Object.values(org.managers).flatMap(m => m.teams),
    }));
  };

  const orgGroups = groupByOrganization(teams);

  const matchesSearch = (u) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (u.name || '').toLowerCase().includes(s) ||
           (u.email || '').toLowerCase().includes(s) ||
           (u.employee_id || '').toLowerCase().includes(s);
  };

  const teamMatchesSearch = (team) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (team.name || '').toLowerCase().includes(s) ||
           (team.lead_name || '').toLowerCase().includes(s) ||
           (team.manager_name || '').toLowerCase().includes(s);
  };

  const toggleManager = (key) => setExpandedManagers(p => ({ ...p, [key]: p[key] === undefined ? false : !p[key] }));
  const toggleTeam = (id) => setExpandedTeams(p => ({ ...p, [id]: !p[id] }));

  // Full team edit
  const openTeamEditor = (team) => {
    // Find the raw team data from allUsers source
    setEditingTeam(team);
    setEditFormData({
      name: team.name || '',
      department: team.department || 'sales',
      lead_id: team.lead_id || '',
      manager_id: team.manager_id || '',
      assistant_manager_id: team.assistant_manager_id || '',
      member_ids: team.member_ids || (team.members || []).map(m => m.id).filter(Boolean),
      sub_teams: team.sub_teams || [],
      budget: team.budget || 0,
      reporting_managers: team.reporting_managers || [],
      onsite_managers: team.onsite_managers || [],
    });
    setEditError('');
    setMemberSearch('');
    setRmSearch('');
    setShowRmModal(true);
  };

  const toggleEditMember = (userId) => {
    setEditFormData(prev => ({
      ...prev,
      member_ids: prev.member_ids.includes(userId)
        ? prev.member_ids.filter(id => id !== userId)
        : [...prev.member_ids, userId],
    }));
  };

  const toggleRm = (userId) => {
    setEditFormData(prev => ({
      ...prev,
      reporting_managers: prev.reporting_managers.includes(userId)
        ? prev.reporting_managers.filter(id => id !== userId)
        : [...prev.reporting_managers, userId],
    }));
  };

  const toggleOm = (userId) => {
    setEditFormData(prev => ({
      ...prev,
      onsite_managers: prev.onsite_managers.includes(userId)
        ? prev.onsite_managers.filter(id => id !== userId)
        : [...prev.onsite_managers, userId],
    }));
  };

  const getLeadOptions = () => {
    const excluded = new Set([editFormData.assistant_manager_id].filter(Boolean));
    return sortUsersByName(allUsers.filter(u => !excluded.has(u.id)));
  };
  const getManagerOptions = () => {
    const excluded = new Set([editFormData.assistant_manager_id].filter(Boolean));
    return sortUsersByName(allUsers.filter(u => !excluded.has(u.id)));
  };
  const getAssistantManagerOptions = () => {
    const excluded = new Set([editFormData.lead_id, editFormData.manager_id].filter(Boolean));
    return sortUsersByName(allUsers.filter(u => !excluded.has(u.id)));
  };
  const getMemberOptions = () => {
    const excluded = new Set([editFormData.lead_id, editFormData.manager_id, editFormData.assistant_manager_id].filter(Boolean));
    return sortUsersByName(allUsers.filter(u => !excluded.has(u.id) || editFormData.member_ids.includes(u.id)));
  };
  const getReportingManagerOptions = () => {
    return sortUsersByName(allUsers.filter(u => ['sales_manager', 'recruiting_manager', 'ceo', 'onsite_manager'].includes(u.role)));
  };
  const getOnsiteManagerOptions = () => {
    return sortUsersByName(allUsers.filter(u => u.role === 'onsite_manager'));
  };

  const saveTeamEdit = async () => {
    if (!editingTeam) return;
    setSavingRm(true);
    setEditError('');
    try {
      if (editingTeam.id) {
        await axios.put(`${API_URL}/api/teams/${editingTeam.id}`, {
          ...editFormData,
          budget: parseFloat(editFormData.budget) || 0,
        }, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/teams`, {
          ...editFormData,
          budget: parseFloat(editFormData.budget) || 0,
        }, { withCredentials: true });
      }
      setShowRmModal(false);
      fetchData();
    } catch (e) { setEditError(e.response?.data?.detail || e.message); }
    finally { setSavingRm(false); }
  };

  const openCreateTeam = () => {
    setEditingTeam({ id: null });
    setEditFormData({
      name: '', department: 'sales', lead_id: '', manager_id: '', assistant_manager_id: '',
      member_ids: [], sub_teams: [], budget: 0, reporting_managers: [], onsite_managers: [],
    });
    setEditError(''); setMemberSearch(''); setRmSearch('');
    setShowRmModal(true);
  };

  const deleteTeam = async (team) => {
    if (!window.confirm(`Delete team "${team.name}"?\n\nThis removes the team but keeps all employees. You can re-create it with the correct Lead & Members.`)) return;
    try {
      await axios.delete(`${API_URL}/api/teams/${team.id}`, { withCredentials: true });
      fetchData();
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="team-overview-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9" data-testid="back-btn"><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight">Team Overview</h1>
            <p className="text-slate-500 mt-1">Organization hierarchy — Manager, Leads, and Members with employee details</p>
          </div>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button onClick={openCreateTeam} className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-team-btn-overview">
              <Plus size={16} /> Create Team
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowExport(true)} className="gap-1.5" data-testid="export-overview-btn">
            <DownloadSimple size={16} /> Export to Excel
          </Button>
          <Input placeholder="Search by name, email, or ID..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full md:w-72" data-testid="team-search" />
        </div>
      </div>

      {orgGroups.length === 0 && (
        <Card className="border-slate-200"><CardContent className="py-12 text-center"><Users size={48} className="mx-auto text-slate-300 mb-3" /><p className="text-slate-500">No teams to display.</p></CardContent></Card>
      )}

      {orgGroups.map((org) => (
        <div key={org.organization} className="space-y-4" data-testid={`org-group-${org.organization}`}>
          {/* Organization Header */}
          <div className="flex items-center justify-between bg-gradient-to-r from-blue-950 to-indigo-900 text-white px-6 py-3 rounded-xl shadow-lg">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
                <UsersThree size={20} className="text-blue-300" />
              </div>
              <div>
                <p className="font-bold text-base tracking-wide cursor-pointer hover:text-blue-300 transition-colors"
                   onClick={() => navigate(`/dashboard/org/${encodeURIComponent(org.organization)}`)}
                   data-testid={`org-link-${org.organization}`}
                >{org.organization}</p>
                <p className="text-[10px] text-blue-300/70 uppercase tracking-widest">{org.managers.length} manager{org.managers.length !== 1 ? 's' : ''} — {org.teams.length} team{org.teams.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>

          {/* Managers within this org */}
          {org.managers.map((mgrGroup) => {
            const mgr = mgrGroup.manager;
            const mgrKey = mgrGroup.manager_name;
            const leadTeams = getTeamsUnderManager(mgrGroup);
            const isExpanded = expandedManagers[mgrKey] !== false; // default expanded
            const groupTotalMargin = leadTeams.reduce((s, t) => s + (t.total_saved_margin || 0), 0);
            const mgrMember = mgrGroup.teams[0]?.manager_member;

            return (
              <Card key={mgrKey} className="border-slate-200 overflow-hidden ml-4" data-testid={`manager-group-${mgrKey}`}>
                {/* Manager Header - clickable to expand/collapse */}
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-5 py-3 cursor-pointer flex items-center justify-between"
                     onClick={() => toggleManager(mgrKey)}>
                  <div className="flex items-center gap-3">
                    <button className="text-slate-400 shrink-0">{isExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}</button>
                    <Crown size={18} className="text-amber-400" />
                    <div>
                      <p className="font-semibold text-sm cursor-pointer hover:text-amber-300 transition-colors"
                         onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/manager/${mgrGroup.manager_id}`); }}
                         data-testid={`mgr-link-${mgrGroup.manager_id}`}
                      >{mgrKey}</p>
                      <p className="text-[10px] text-slate-300 uppercase tracking-wider">Manager — {leadTeams.length} team{leadTeams.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="hidden md:flex gap-6 text-xs text-slate-300">
                    {mgr?.email && <span className="flex items-center gap-1"><EnvelopeSimple size={12} /> {mgr.email}</span>}
                    {mgr?.employee_id && <span>ID: {mgr.employee_id}</span>}
                  </div>
                </div>

                {isExpanded && (
                  <div className="divide-y divide-slate-100">
                    {/* Manager's own data row as a table */}
                    {mgrMember && (
                      <div className="bg-amber-50/50 border-b border-amber-100">
                        <div className="px-5 py-2 flex items-center gap-2">
                          <Crown size={14} className="text-amber-600" />
                          <span className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Manager</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-amber-100/60">
                                <th className="px-3 py-1.5 text-left font-semibold">First Name</th>
                                <th className="px-3 py-1.5 text-left font-semibold">Last Name</th>
                                <th className="px-3 py-1.5 text-left font-semibold">Emp ID</th>
                                <th className="px-3 py-1.5 text-left font-semibold">Email</th>
                                <th className="px-3 py-1.5 text-left font-semibold">Organization</th>
                                <th className="px-3 py-1.5 text-left font-semibold">Role</th>
                                <th className="px-3 py-1.5 text-left font-semibold">Contact</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="bg-amber-50/30 hover:bg-amber-50" data-testid={`manager-row-${mgrMember.id}`}>
                                <td className="px-3 py-2 font-medium text-slate-800">{mgrMember.first_name}</td>
                                <td className="px-3 py-2 text-slate-700">{mgrMember.last_name}</td>
                                <td className="px-3 py-2 font-mono text-slate-600">{mgrMember.employee_id || '-'}</td>
                                <td className="px-3 py-2 text-slate-600">{mgrMember.email}</td>
                                <td className="px-3 py-2 text-slate-600">{mgrMember.organization || '-'}</td>
                                <td className="px-3 py-2"><Badge className="bg-amber-100 text-amber-800 text-[10px]">{getRoleLabel(mgrMember.role)}</Badge></td>
                                <td className="px-3 py-2 text-slate-600">{mgrMember.phone || '-'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Teams under this manager (lead teams only) */}
                    {leadTeams.filter(teamMatchesSearch).map((team) => {
                      const isTeamExpanded = expandedTeams[team.id] !== false;
                      const lead = team.members.find(m => m.id === team.lead_id) || (team.lead_name ? { name: team.lead_name } : null);
                      const asstMgr = team.members.find(m => m.id === team.assistant_manager_id) || (team.assistant_manager_name ? { name: team.assistant_manager_name } : null);
                      // Hide Lead, Asst Mgr, and the Manager themselves from the member rows —
                      // they're already shown in their own dedicated header sections above.
                      const allMembers = team.members.filter(m =>
                        m.id !== team.lead_id &&
                        m.id !== team.assistant_manager_id &&
                        m.id !== team.manager_id
                      ).filter(matchesSearch);

                      return (
                        <div key={team.id} data-testid={`team-section-${team.id}`}>
                          <div className="px-5 py-3 hover:bg-slate-50 cursor-pointer transition-colors flex items-center gap-4" onClick={() => toggleTeam(team.id)}>
                            <button className="text-slate-400 shrink-0">{isTeamExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}</button>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-900">{team.name}</span>
                                <Badge className={`text-[10px] ${team.department === 'sales' ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'}`}>{team.department}</Badge>
                                <Badge variant="outline" className="text-[10px]">{allMembers.length} members</Badge>
                              </div>
                              <div className="flex flex-wrap gap-4 text-[11px] text-slate-500 mt-0.5">
                                {lead && <span>Lead: <strong className="text-slate-700">{lead.name}</strong></span>}
                                {asstMgr && <span>Asst. Mgr: <strong className="text-slate-700">{asstMgr.name}</strong></span>}
                                {(team.reporting_manager_names || []).length > 0 && <span>Reporting Mgrs: <strong className="text-slate-700">{team.reporting_manager_names.join(', ')}</strong></span>}
                                {(team.onsite_manager_names || []).length > 0 && <span>Onsite Mgrs: <strong className="text-slate-700">{team.onsite_manager_names.join(', ')}</strong></span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {isAdmin && (
                                <>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={(e) => { e.stopPropagation(); openTeamEditor(team); }} data-testid={`edit-team-${team.id}`}>
                                    <PencilSimple size={14} />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); deleteTeam(team); }} data-testid={`delete-team-${team.id}`} title="Delete team">
                                    <X size={14} />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>

                          {isTeamExpanded && (
                            <div className="bg-slate-50/70 border-t border-slate-100">
                              {lead && (
                                <div className="px-6 py-2 bg-purple-50/50 border-b border-purple-100 flex items-center gap-2">
                                  <UserCircle size={16} className="text-purple-600" />
                                  <span className="text-xs font-semibold text-purple-700">Team Lead: {lead.name}</span>
                                  {lead.email && <span className="text-[10px] text-purple-500 flex items-center gap-1"><EnvelopeSimple size={10} /> {lead.email}</span>}
                                  {lead.employee_id && <Badge variant="outline" className="text-[10px]">ID: {lead.employee_id}</Badge>}
                                </div>
                              )}

                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-100/80">
                                      <th className="px-3 py-2 text-left font-semibold">First Name</th>
                                      <th className="px-3 py-2 text-left font-semibold">Last Name</th>
                                      <th className="px-3 py-2 text-left font-semibold">Emp ID</th>
                                      <th className="px-3 py-2 text-left font-semibold">Email</th>
                                      <th className="px-3 py-2 text-left font-semibold">Organization</th>
                                      <th className="px-3 py-2 text-left font-semibold">Role</th>
                                      <th className="px-3 py-2 text-left font-semibold">Contact</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-50">
                                    {allMembers.map((m) => {
                                      const isLeadMember = m.id === team.lead_id;
                                      const isAsstMgr = m.id === team.assistant_manager_id;
                                      return (
                                        <tr key={m.id} className={`hover:bg-white/80 transition-colors ${isLeadMember ? 'bg-purple-50/30' : isAsstMgr ? 'bg-indigo-50/30' : ''}`} data-testid={`employee-row-${m.id}`}>
                                          <td className="px-3 py-2 font-medium text-slate-800">{m.first_name || m.name?.split(' ')[0] || ''}</td>
                                          <td className="px-3 py-2 text-slate-700">{m.last_name || m.name?.split(' ').slice(1).join(' ') || ''}</td>
                                          <td className="px-3 py-2 font-mono text-slate-600">{m.employee_id || '-'}</td>
                                          <td className="px-3 py-2 text-slate-600">{m.email}</td>
                                          <td className="px-3 py-2 text-slate-600">{m.organization || '-'}</td>
                                          <td className="px-3 py-2">
                                            <Badge variant="outline" className="text-[10px]">{getRoleLabel(m.role)}</Badge>
                                            {isLeadMember && <Badge className="ml-1 bg-purple-100 text-purple-700 text-[9px]">Lead</Badge>}
                                            {isAsstMgr && <Badge className="ml-1 bg-indigo-100 text-indigo-700 text-[9px]">Asst</Badge>}
                                          </td>
                                          <td className="px-3 py-2 text-slate-600">{m.phone || '-'}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                              {allMembers.length === 0 && (
                                <p className="px-6 py-4 text-sm text-slate-400 text-center">{search ? 'No members match search' : 'No members in this team'}</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      ))}

      {/* Full Team Edit Modal */}
      <Dialog open={showRmModal} onOpenChange={setShowRmModal}>
        <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col gap-0 p-0 overflow-hidden">
          <div className="shrink-0 bg-white border-b border-slate-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Outfit'] font-bold">{editingTeam?.id ? 'Edit Team' : 'Create Team'}</DialogTitle>
              <DialogDescription>{editingTeam?.id ? <>Update team details, hierarchy, and members for <strong>{editingTeam?.name}</strong></> : 'Set up a new team with Lead, Manager, and Members.'}</DialogDescription>
            </DialogHeader>
          </div>
          <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            {editError && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{editError}</div>}

            {/* Row 1: Name + Department */}
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Team Name *</Label>
                <Input value={editFormData.name} onChange={(e) => setEditFormData({...editFormData, name: e.target.value})} className="h-10" data-testid="edit-team-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Department *</Label>
                <Select value={editFormData.department} onValueChange={(v) => setEditFormData({...editFormData, department: v, lead_id: '', manager_id: '', assistant_manager_id: '', member_ids: []})}>
                  <SelectTrigger className="h-10" data-testid="edit-dept"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="recruiting">Recruiting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Lead + Manager */}
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Lead</Label>
                <Select value={editFormData.lead_id || 'none'} onValueChange={(v) => setEditFormData({...editFormData, lead_id: v === 'none' ? '' : v})}>
                  <SelectTrigger className="h-10" data-testid="edit-lead"><SelectValue placeholder="Select lead..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No lead assigned</SelectItem>
                    {getLeadOptions().map(u => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-slate-400 ml-1">({u.email})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Manager</Label>
                <Select value={editFormData.manager_id || 'none'} onValueChange={(v) => setEditFormData({...editFormData, manager_id: v === 'none' ? '' : v})}>
                  <SelectTrigger className="h-10" data-testid="edit-manager"><SelectValue placeholder="Select manager..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No manager assigned</SelectItem>
                    {getManagerOptions().map(u => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-slate-400 ml-1">({u.email})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 3: Asst Manager + Budget */}
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Assistant Manager</Label>
                <Select value={editFormData.assistant_manager_id || 'none'} onValueChange={(v) => setEditFormData({...editFormData, assistant_manager_id: v === 'none' ? '' : v})}>
                  <SelectTrigger className="h-10" data-testid="edit-asst-mgr"><SelectValue placeholder="Select assistant manager..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No assistant manager</SelectItem>
                    {getAssistantManagerOptions().map(u => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-slate-400 ml-1">({u.email})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Monthly Budget</Label>
                <Input type="number" value={editFormData.budget} onChange={(e) => setEditFormData({...editFormData, budget: e.target.value})} className="h-10" data-testid="edit-budget" />
              </div>
            </div>

            {/* Team Members — Chip-based */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Team Members</Label>
                <span className="text-xs text-slate-400">{editFormData.member_ids.length} member(s)</span>
              </div>
              {editFormData.member_ids.length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="edit-selected-members">
                  {editFormData.member_ids.map(uid => {
                    const u = allUsers.find(x => x.id === uid);
                    if (!u) return null;
                    return (
                      <span key={uid} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 bg-blue-600 text-white text-xs font-medium rounded-full shadow-sm hover:bg-blue-700 transition-colors" data-testid={`edit-chip-${uid}`}>
                        {u.name}
                        <button type="button" onClick={() => toggleEditMember(uid)} className="ml-0.5 p-0.5 rounded-full hover:bg-blue-800 transition-colors" data-testid={`edit-remove-${uid}`}>
                          <X size={12} weight="bold" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="Search by name or email to add members..."
                  className="pl-9 h-9 text-sm"
                  data-testid="edit-member-search"
                />
              </div>
              {memberSearch.trim() ? (
                <div className="border border-slate-200 rounded-lg max-h-44 overflow-y-auto shadow-sm" data-testid="edit-member-results">
                  {(() => {
                    const q = memberSearch.toLowerCase();
                    const results = getMemberOptions().filter(u =>
                      !editFormData.member_ids.includes(u.id) &&
                      (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
                    );
                    if (results.length === 0) return <p className="p-3 text-sm text-slate-400 text-center">No matching members found</p>;
                    return results.slice(0, 15).map(u => (
                      <button
                        key={u.id} type="button"
                        onClick={() => { toggleEditMember(u.id); setMemberSearch(''); }}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-blue-50 text-left border-b border-slate-50 last:border-0 transition-colors"
                        data-testid={`edit-add-${u.id}`}
                      >
                        <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {u.name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
                          <p className="text-xs text-slate-400 truncate">{u.email}{u.team ? ` · ${u.team}` : ''}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px] flex-shrink-0">{getRoleLabel(u.role)}</Badge>
                        <Plus size={14} className="text-blue-500 flex-shrink-0" />
                      </button>
                    ));
                  })()}
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg max-h-44 overflow-y-auto">
                  {getMemberOptions().length === 0 ? (
                    <p className="p-3 text-sm text-slate-400 text-center">No available members for this department</p>
                  ) : (
                    <div className="divide-y divide-slate-50">
                      {getMemberOptions().map(u => (
                        <label key={u.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors">
                          <Checkbox checked={editFormData.member_ids.includes(u.id)} onCheckedChange={() => toggleEditMember(u.id)} />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-slate-700">{u.name}</span>
                            <span className="text-xs text-slate-400 ml-1.5">{u.email}</span>
                          </div>
                          {u.team && <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{u.team}</span>}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Reporting Managers — Chip-based */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reporting Managers <span className="font-normal text-slate-400">(CEO, Managers, Onsite)</span></Label>
                <span className="text-xs text-slate-400">{(editFormData.reporting_managers || []).length} selected</span>
              </div>
              {(editFormData.reporting_managers || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  {(editFormData.reporting_managers || []).map(uid => {
                    const u = allUsers.find(x => x.id === uid);
                    if (!u) return null;
                    return (
                      <span key={uid} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 bg-amber-600 text-white text-xs font-medium rounded-full shadow-sm hover:bg-amber-700 transition-colors">
                        {u.name}
                        <button type="button" onClick={() => toggleRm(uid)} className="ml-0.5 p-0.5 rounded-full hover:bg-amber-800 transition-colors">
                          <X size={12} weight="bold" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={rmSearch}
                  onChange={(e) => setRmSearch(e.target.value)}
                  placeholder="Search reporting managers..."
                  className="pl-9 h-9 text-sm"
                  data-testid="edit-rm-search"
                />
              </div>
              <div className="border border-slate-200 rounded-lg max-h-36 overflow-y-auto">
                {(() => {
                  const q = rmSearch.toLowerCase();
                  const opts = getReportingManagerOptions().filter(u =>
                    !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
                  );
                  if (opts.length === 0) return <p className="p-3 text-sm text-slate-400 text-center">No matching reporting managers</p>;
                  return opts.map(u => (
                    <label key={u.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-50 last:border-0">
                      <Checkbox checked={(editFormData.reporting_managers || []).includes(u.id)} onCheckedChange={() => toggleRm(u.id)} data-testid={`edit-rm-${u.id}`} />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-slate-700">{u.name}</span>
                        <span className="text-xs text-slate-400 ml-1.5">{u.email}</span>
                      </div>
                      <Badge variant="outline" className="text-[10px]">{getRoleLabel(u.role)}</Badge>
                    </label>
                  ));
                })()}
              </div>
            </div>

            {/* Onsite Managers — Chip-based */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Onsite Managers</Label>
                <span className="text-xs text-slate-400">{(editFormData.onsite_managers || []).length} selected</span>
              </div>
              {(editFormData.onsite_managers || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  {(editFormData.onsite_managers || []).map(uid => {
                    const u = allUsers.find(x => x.id === uid);
                    if (!u) return null;
                    return (
                      <span key={uid} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 bg-purple-600 text-white text-xs font-medium rounded-full shadow-sm hover:bg-purple-700 transition-colors">
                        {u.name}
                        <button type="button" onClick={() => toggleOm(uid)} className="ml-0.5 p-0.5 rounded-full hover:bg-purple-800 transition-colors">
                          <X size={12} weight="bold" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="border border-slate-200 rounded-lg max-h-32 overflow-y-auto">
                {getOnsiteManagerOptions().length === 0 ? (
                  <p className="p-3 text-sm text-slate-400 text-center">No onsite managers available</p>
                ) : (
                  getOnsiteManagerOptions().map(u => (
                    <label key={u.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-50 last:border-0">
                      <Checkbox checked={(editFormData.onsite_managers || []).includes(u.id)} onCheckedChange={() => toggleOm(u.id)} data-testid={`edit-om-${u.id}`} />
                      <span className="text-sm font-medium text-slate-700">{u.name}</span>
                      <span className="text-xs text-slate-400">{u.email}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Sub Teams */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sub Teams</Label>
              <Input
                value={(editFormData.sub_teams || []).join(', ')}
                onChange={(e) => setEditFormData({...editFormData, sub_teams: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
                placeholder="Sub Team A, Sub Team B"
                className="h-10"
                data-testid="edit-sub-teams"
              />
              <p className="text-xs text-slate-400">Comma-separated sub-team names</p>
            </div>
            </div>
            <div className="shrink-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => { setShowRmModal(false); setMemberSearch(''); setRmSearch(''); }}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 min-w-[120px]" onClick={saveTeamEdit} disabled={savingRm} data-testid="save-team-edit-btn">
                {savingRm && <SpinnerGap size={16} className="animate-spin mr-2" />} Update Team
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export all members across all teams */}
      <ExportPreviewModal open={showExport} onClose={() => setShowExport(false)}
        data={teams.flatMap(t => t.members.map(m => ({ ...m, team_name: t.name, department: t.department })))}
        fileName={`Team_Overview_${new Date().toISOString().slice(0,10)}`}
        title="Team Overview Export Preview"
        columns={[
          { key: 'team', header: 'Team', accessor: r => r.team_name },
          { key: 'dept', header: 'Department', accessor: r => r.department },
          { key: 'first', header: 'First Name', accessor: r => r.first_name || r.name?.split(' ')[0] || '' },
          { key: 'last', header: 'Last Name', accessor: r => r.last_name || r.name?.split(' ').slice(1).join(' ') || '' },
          { key: 'emp_id', header: 'Emp ID', accessor: r => r.employee_id || '' },
          { key: 'email', header: 'Email', accessor: r => r.email },
          { key: 'role', header: 'Role', accessor: r => getRoleLabel(r.role) },
          { key: 'org', header: 'Organization', accessor: r => r.organization || '' },
          { key: 'salary', header: 'Salary (INR)', accessor: r => r.salary || '' },
        ]}
      />
    </div>
  );
};

export default TeamOverviewPage;
